import styled from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken } from '@optimus-web/common';
import { ANIMATION_CURVE, ANIMATION_DURATION } from '@optimus-web/local-tokens';

const TRANSITION = `
    transition-property: background-color, border-radius, border-color, opacity;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.default}ms;
`;

export const TableElement = styled.table`
    border-spacing: ${getToken(TokenName.SPACING_0)};
    width: 100%;
    height: 1px;
`;

export const TableHeader = styled.thead<{ fixedHeader: boolean }>`
    position: ${(props) => (props.fixedHeader ? 'sticky' : 'relative')};
    background: ${({ fixedHeader }) =>
        fixedHeader
            ? getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_ACTIVE)
            : getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_DEFAULT)};
    top: ${(props) => (props.fixedHeader ? '0' : 'initial')};
    z-index: ${(props) => (props.fixedHeader ? '100' : 'initial')};
    ${({ fixedHeader, theme }) => fixedHeader && `box-shadow: ${getToken(TokenName.SHADOW_100)({ theme })}`};
`;

export const TableHeaderRow = styled.tr``;

export const TableHeaderCell = styled.th<{
    fixedFirstColumn: boolean;
    fixedHeader: boolean;
    interactive: boolean;
    rowSelectionEnabled: boolean;
    width?: number;
    filterable?: boolean;
}>`
    ${getTextTokenPreset('BODY_M_STRONG')};
    background: ${getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_DEFAULT)};
    padding: ${getToken(TokenName.SPACING_0)}px ${getToken(TokenName.SPACING_200)}px;
    padding-right: ${({ filterable }) =>
        filterable ? getToken(TokenName.SPACING_100) : getToken(TokenName.SPACING_200)}px;
    min-height: ${getToken(TokenName.SPACING_500)}px;
    border-bottom: ${getToken(TokenName.BORDER_WIDTH_250)}px solid ${getToken(TokenName.BORDER_STATIC_SECONDARY)};
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    width: ${({ width, theme }) =>
        width != null ? `${width - 2 * getToken(TokenName.SPACING_200)({ theme })}px` : 'auto'};
    ${TRANSITION};

    &:first-child {
        border-top-left-radius: ${getToken(TokenName.BORDER_RADIUS_100)}px;
        position: ${({ fixedFirstColumn }) => (fixedFirstColumn ? 'sticky' : 'relative')};
        z-index: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '10' : 'initial')};
        left: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '0' : 'initial')};
        width: ${({ rowSelectionEnabled, width, theme }) => {
            if (rowSelectionEnabled) {
                return `${getToken(TokenName.SPACING_300)({ theme })}px`;
            }
            return width != null ? `${width - 2 * getToken(TokenName.SPACING_200)({ theme })}px` : 'auto';
        }};
    }

    &:last-child {
        border-top-right-radius: ${getToken(TokenName.BORDER_RADIUS_100)}px;
    }

    &:nth-of-type(2) {
        position: ${({ fixedFirstColumn, rowSelectionEnabled }) =>
            fixedFirstColumn && rowSelectionEnabled ? 'sticky' : 'relative'};
    }
`;

export const TableBody = styled.tbody<{ isLoading: boolean }>`
    pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'initial')};
`;

export const TableCellInner = styled.div<{ align?: 'start' | 'end' }>`
    min-height: ${getToken(TokenName.SPACING_700)}px;
    height: 100%;
    padding: ${getToken(TokenName.SPACING_100)}px ${getToken(TokenName.SPACING_200)}px;
    display: flex;
    align-items: center;
    justify-content: ${({ align }) => (align ? `flex-${align}` : 'flex-start')};
    ${TRANSITION};
`;

export const TableCell = styled.td<{ fixedFirstColumn: boolean; rowSelectionEnabled: boolean }>`
    height: 100%;
    ${getTextTokenPreset('BODY_M')};
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    padding: ${getToken(TokenName.SPACING_0)};
    ${TRANSITION};

    &:first-child {
        background-color: ${getToken(TokenName.BACKGROUND_STATIC_FLOATING)};
        position: ${({ fixedFirstColumn }) => (fixedFirstColumn ? 'sticky' : 'relative')};
        z-index: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '10' : 'initial')};
        left: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '0' : 'initial')};
    }

    &:nth-of-type(2) {
        position: ${({ fixedFirstColumn, rowSelectionEnabled }) =>
            fixedFirstColumn && rowSelectionEnabled ? 'sticky' : 'relative'};
    }
`;

export const TableBodyRow = styled.tr<{ active?: boolean; nextIsActive?: boolean; previousIsActive?: boolean }>`
    position: relative;
    height: 100%;
    /* fix browsers like Safari ignoring relative position for table, tr, td, etc. elements. See https://www.w3.org/TR/CSS2/visuren.html#choose-position */
    transform: scale(1, 1);

    ${TableCell} {
        border-bottom: 1px solid
            ${({ active }) =>
                active ? getToken(TokenName.BORDER_STATIC_INVERSE) : getToken(TokenName.BORDER_STATIC_SECONDARY)};

        &:first-child {
            ${TableCellInner} {
                border-top-left-radius: ${({ active, previousIsActive, theme }) =>
                    active && previousIsActive
                        ? `${getToken(TokenName.BORDER_RADIUS_0)({ theme })}px`
                        : `${getToken(TokenName.BORDER_RADIUS_100)({ theme })}px`};
                border-bottom-left-radius: ${({ active, nextIsActive, theme }) =>
                    active && nextIsActive
                        ? `${getToken(TokenName.BORDER_RADIUS_0)({ theme })}px`
                        : `${getToken(TokenName.BORDER_RADIUS_100)({ theme })}px`};
            }
        }

        &:last-child {
            ${TableCellInner} {
                border-top-right-radius: ${({ active, previousIsActive, theme }) =>
                    active && previousIsActive
                        ? `${getToken(TokenName.BORDER_RADIUS_0)({ theme })}px`
                        : `${getToken(TokenName.BORDER_RADIUS_100)({ theme })}px`};
                border-bottom-right-radius: ${({ active, nextIsActive, theme }) =>
                    active && nextIsActive
                        ? `${getToken(TokenName.BORDER_RADIUS_0)({ theme })}px`
                        : `${getToken(TokenName.BORDER_RADIUS_100)({ theme })}px`};
            }
        }
    }

    ${TableCellInner} {
        background-color: ${({ active }) =>
            active
                ? getToken(TokenName.BACKGROUND_INTERACTIVE_SECONDARY_DEFAULT)
                : getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_DEFAULT)};
    }

    &::after {
        content: '';
        width: 100%;
        height: ${getToken(TokenName.SPACING_25)}px;
        background-color: ${getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_DEFAULT)};
        position: absolute;
        top: -1px;
        left: 0;
        opacity: ${({ active }) => (active ? getToken(TokenName.OPACITY_1000) : getToken(TokenName.OPACITY_0))};
        ${TRANSITION};
        z-index: 12;
    }

    &:first-child {
        &::after {
            height: 0;
        }

        ${TableCell} {
            &:first-child {
                ${TableCellInner} {
                    border-top-left-radius: ${getToken(TokenName.BORDER_RADIUS_0)}px;
                }
            }

            &:last-child {
                ${TableCellInner} {
                    border-top-right-radius: ${getToken(TokenName.BORDER_RADIUS_0)}px;
                }
            }
        }
    }

    &:hover {
        &::after {
            opacity: ${getToken(TokenName.OPACITY_1000)};
            height: ${getToken(TokenName.SPACING_25)}px;
        }

        ${TableCell} {
            border-bottom: 1px solid ${getToken(TokenName.BORDER_STATIC_INVERSE)};
        }

        &:first-child {
            &::after {
                opacity: ${getToken(TokenName.OPACITY_1000)};
                height: ${getToken(TokenName.SPACING_0)};
            }

            ${TableCell} {
                &:first-child {
                    ${TableCellInner} {
                        border-top-left-radius: ${getToken(TokenName.BORDER_RADIUS_0)}px;
                    }
                }

                &:last-child {
                    ${TableCellInner} {
                        border-top-right-radius: ${getToken(TokenName.BORDER_RADIUS_0)}px;
                    }
                }
            }
        }

        ${TableCellInner} {
            background-color: ${({ active }) =>
                active
                    ? getToken(TokenName.BACKGROUND_INTERACTIVE_SECONDARY_HOVER)
                    : getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_HOVER)};
        }
    }
`;

export const TableContainer = styled.div`
    overflow: auto;
    max-width: 100%;
    max-height: 100%;
`;

export const TableLoading = styled.div<{ isLoading: boolean }>`
    visibility: ${({ isLoading }) => (isLoading ? 'visible' : 'hidden')};
    background-color: ${getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_DEFAULT)};
    ${({ isLoading, theme }) => isLoading && `opacity: ${getToken(TokenName.OPACITY_800)({ theme })};`}
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

export const TableWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;
